/**
 * MoonDreamDeckFit — how a finished summoner's spell deck is cut into pages.
 *
 * THE MIDDLE GROUND (owner, 3 October 2026). The deck was one page of four
 * cards at every size ("weird and ugly"); a first redesign dealt as many cards
 * as its box could hold, 24 a page at 1920x1080 ("too crowded"). A page now
 * has a cap — two rows of four unless the layout sets another — and holds the
 * most cards up to that cap that keep every card at least `minCardWidth`
 * wide, all at one size, as large as the box allows. A deck that fits on one
 * page takes the largest card that still holds all of it.
 *
 * Pure: the caller measures the box and owns the DOM.
 */
let MoonDreamDeckFit = {};

MoonDreamDeckFit = (function () {
  /* Without a measurable box (no layout: jsdom, a hidden panel) the deck keeps
     the page it had before measurement existed. */
  const UNMEASURED_PAGE_SIZE = 4;
  const DEFAULT_MAX_ROWS = 2;
  const DEFAULT_MAX_COLUMNS = 4;

  function nonNegative(value) {
    const numeric = Number(value);
    return Number.isFinite(numeric) && numeric > 0 ? numeric : 0;
  }

  function wholeAtLeastOne(value, fallback) {
    const numeric = Math.floor(Number(value));
    return Number.isFinite(numeric) && numeric >= 1 ? numeric : fallback;
  }

  /**
   * A box without a measurable width, height or aspect is "unmeasured".
   * @param {object} [box]
   * @param {number} [box.width]      the deck grid's content width (CSS px)
   * @param {number} [box.height]     the deck grid's content height (CSS px)
   * @param {number} [box.columnGap]
   * @param {number} [box.rowGap]
   * @param {number} [box.aspect]     card width / card height
   * @param {number} [box.minCardWidth] the narrowest card a page may deal
   * @param {number} [box.total]      spells in the deck
   * @param {number} [box.maxRows]    the page's row cap
   * @param {number} [box.maxColumns] the page's column cap
   * @param {number} [box.sizeStep]   a deck that pages anyway gives up one card
   *   a row for a card at least this share larger (0: never)
   */
  function solve({
    width,
    height,
    columnGap = 0,
    rowGap = 0,
    aspect,
    minCardWidth = 0,
    total = Number.POSITIVE_INFINITY,
    maxRows = DEFAULT_MAX_ROWS,
    maxColumns = DEFAULT_MAX_COLUMNS,
    sizeStep = 0,
  } = {}) {
    const boxWidth = nonNegative(width);
    const boxHeight = nonNegative(height);
    const ratio = nonNegative(aspect);
    if (!boxWidth || !boxHeight || !ratio) {
      return { rows: 0, columns: 0, pageSize: UNMEASURED_PAGE_SIZE, cardWidth: 0, cardHeight: 0, measured: false };
    }
    const gapX = nonNegative(columnGap);
    const gapY = nonNegative(rowGap);
    const floor = nonNegative(minCardWidth);
    const rowCap = wholeAtLeastOne(maxRows, DEFAULT_MAX_ROWS);
    const columnCap = wholeAtLeastOne(maxColumns, DEFAULT_MAX_COLUMNS);
    const deckSize = Number(total) > 0 ? Number(total) : Number.POSITIVE_INFINITY;

    // Every page shape under the cap, each card as large as the box allows it.
    const candidates = [];
    for (let rows = 1; rows <= rowCap; rows += 1) {
      const rowHeight = (boxHeight - (rows - 1) * gapY) / rows;
      if (!(rowHeight > 0)) break;
      for (let columns = 1; columns <= columnCap; columns += 1) {
        const cardWidth = Math.min(rowHeight * ratio, (boxWidth - (columns - 1) * gapX) / columns);
        if (!(cardWidth > 0) || cardWidth < floor) continue;
        candidates.push({ rows, columns, cardWidth });
      }
    }
    // A box smaller than one floor-sized card still shows one card that fits.
    if (candidates.length === 0) {
      candidates.push({ rows: 1, columns: 1, cardWidth: Math.min(boxHeight * ratio, boxWidth) });
    }

    const capacity = (candidate) => candidate.rows * candidate.columns;
    const larger = (a, b) => (b.cardWidth > a.cardWidth ? b : a);
    const holdsAll = candidates.filter((candidate) => capacity(candidate) >= deckSize);
    let best = holdsAll.length > 0
      ? holdsAll.reduce(larger)
      : candidates.reduce((a, b) => (
        capacity(b) > capacity(a) || (capacity(b) === capacity(a) && b.cardWidth > a.cardWidth) ? b : a
      ));
    /* A deck that pages anyway may trade one card a row for a card at least
       `sizeStep` larger (screen-fit round 3, 28 October 2026: on a phone,
       five 63px cards over four 70px ones, but four 104px over five 84px).
       One step only, so a roomy box never cascades down to a card a page. */
    const step = nonNegative(sizeStep);
    if (holdsAll.length === 0 && step > 0) {
      const fewer = candidates.find((candidate) => (
        candidate.rows === best.rows && candidate.columns === best.columns - 1
      ));
      if (fewer && fewer.cardWidth >= best.cardWidth * (1 + step)) best = fewer;
    }
    return {
      rows: best.rows,
      columns: best.columns,
      pageSize: capacity(best),
      cardWidth: best.cardWidth,
      cardHeight: best.cardWidth / ratio,
      measured: true,
    };
  }

  return { solve, UNMEASURED_PAGE_SIZE, DEFAULT_MAX_ROWS, DEFAULT_MAX_COLUMNS };
})();

export { MoonDreamDeckFit };
